import React, { ReactElement, useEffect, useMemo } from 'react'
import debounce from 'lodash/debounce'
import { ButtonWithLoadingState } from 'components/ButtonWithLoadingState'
import { Svg } from 'ui'
import GoogleIcon from 'images/google-icon.svg'
import { useLazyGetGoogleAuthorizationUrlQuery } from 'store/api/user/user'
import { Text } from 'theme-ui'

export function LoginSignUpGoogleButton({ onClick }: { onClick?: () => void }): ReactElement {
  const [
    getGoogleAuthorizationUrl,
    { data: googleAuthorizationUrl, isFetching: isGoogleAuthorizationUrlFetching },
  ] = useLazyGetGoogleAuthorizationUrlQuery()

  const debouncedGetGoogleAuthorizationUrl = useMemo(
    () =>
      debounce(() => getGoogleAuthorizationUrl(''), 500, {
        leading: true,
        trailing: false,
      }),
    [getGoogleAuthorizationUrl],
  )

  const handleClick = () => {
    onClick?.()
    debouncedGetGoogleAuthorizationUrl()
  }

  useEffect(() => {
    if (googleAuthorizationUrl && !isGoogleAuthorizationUrlFetching) {
      window.location.href = googleAuthorizationUrl.url
    }
  }, [googleAuthorizationUrl, isGoogleAuthorizationUrlFetching])

  return (
    <ButtonWithLoadingState
      isLoading={isGoogleAuthorizationUrlFetching}
      data-testid="google-login-button"
      disabled={isGoogleAuthorizationUrlFetching}
      ariaLabel="Continue with Google"
      onClick={handleClick}
      sx={{
        width: '100%',
        py: '1rem',
        px: ['0.4rem', '1.2rem'],
        backgroundColor: 'eerieBlack',
        color: 'aria',
        gap: '1.6rem',
        borderRadius: '50px',
        border: '1px solid',
        borderColor: 'taupeGray',
        ':hover': {
          ':not([disabled])': {
            cursor: 'pointer',
            backgroundColor: 'eerieBlack80',
            transform: 'none',
            '& svg path': {
              fill: 'revert-layer',
            },
          },
        },
        ':disabled': {
          cursor: 'auto',
          borderColor: 'white8',
        },
      }}
    >
      <Svg descriptiveText="google icon" svg={GoogleIcon} />
      <Text variant="size17Weight600Line138">Continue with Google</Text>
    </ButtonWithLoadingState>
  )
}
